import * as React from 'react';

import {
  Box,
  FormHelperText,
  InputAdornment,
  Slider,
  TextField,
  Typography,
} from '@mui/material';

import styles from './GenderSplitFields.module.css';

export interface GenderSplitFieldsProps {
  /** Share of female panelists in [0, 1]. */
  female: number;
  onChange: (female: number) => void;
  disabled?: boolean;
  error?: string;
}

const MARKS = [0, 25, 50, 75, 100].map((value) => ({ value }));

/**
 * The female share, typed or slid; male is always the rest, so the split adds
 * up to 100%. Only whole numbers 0–100 are accepted.
 */
const GenderSplitFields: React.FC<GenderSplitFieldsProps> = ({
  female,
  onChange,
  disabled,
  error,
}) => {
  const femalePercent = Math.min(Math.max(Math.round(female * 100), 0), 100);
  // While typing, the field may be briefly empty; the split itself only ever holds a valid value.
  const [draft, setDraft] = React.useState<string | null>(null);

  const type = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, 3);
    if (digits === '') {
      setDraft('');
      return;
    }
    const percent = Math.min(Number(digits), 100);
    setDraft(String(percent));
    onChange(percent / 100);
  };

  return (
    <Box>
      <Box className={styles.row}>
        <TextField
          label="Female"
          size="small"
          value={draft ?? String(femalePercent)}
          disabled={disabled}
          error={!!error}
          onChange={(e) => type(e.target.value)}
          onFocus={(e) => e.target.select()}
          onBlur={() => setDraft(null)}
          className={styles.field}
          InputProps={{
            endAdornment: <InputAdornment position="end">%</InputAdornment>,
          }}
          inputProps={{
            inputMode: 'numeric',
            'aria-label': 'Female share of panel',
          }}
        />
        <Slider
          aria-label="Female share of panel slider"
          value={femalePercent}
          min={0}
          max={100}
          step={1}
          marks={MARKS}
          disabled={disabled}
          getAriaValueText={(v) => `${v}% female, ${100 - v}% male`}
          onChange={(_, v) => {
            setDraft(null);
            onChange((v as number) / 100);
          }}
          color={error ? 'error' : 'primary'}
          className={styles.slider}
        />
        <Typography variant="body2" className={styles.male} aria-live="polite">
          <strong>{100 - femalePercent}%</strong> male
        </Typography>
      </Box>
      {error && <FormHelperText error>{error}</FormHelperText>}
    </Box>
  );
};

export default GenderSplitFields;
